import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Spinner, Input, Button, Tooltip } from "@heroui/react";
import { Typography, UniversalTable, IdView } from "../../../../../components";
import { FilterSelect } from "../../../../../components/ui";
import {
  refShareApi,
  RefShareOrder,
  OrderStatus,
} from "../../../../../api/services";
import { showError, showPartnerSuccess } from "../../../../../utils/toast";
import { parseApiError } from "../../../../../utils/errorParser";
import { useTranslations } from "../../../../../contexts/translation.context";
import {
  EmptyContentIc,
  SearchInputIc,
  DangerIc,
  ResetFiltersIc,
} from "../../../../../components/icons";
import OwnerCell from "../../../../revshare/campaigns/components/OwnerCell";
import CampaignCell from "../../../../revshare/campaigns/components/CampaignCell";
import CampaignStatisticsCell from "../../../../revshare/campaigns/components/CampaignStatisticsCell";
import PlatformIncomeCell from "../../../../revshare/campaigns/components/PlatformIncomeCell";
import CampaignStatusCell from "../../../../revshare/campaigns/components/CampaignStatusCell";
import MarketerCell from "../../../../revshare/campaigns/components/MarketerCell";
import CampaignActionsCell from "../../../../revshare/campaigns/components/CampaignActionsCell";

interface CreatorRevShareCampaignsTableProps {
  creatorId: string;
}

interface RevShareFilters {
  search: string;
  status: string;
}

const DEFAULT_FILTERS: RevShareFilters = {
  search: '',
  status: 'all',
};

const SEARCH_DEBOUNCE_DELAY = 500;
const PLATFORM_COMMISSION = 5;

/**
 * CreatorRevShareCampaignsTable Component
 *
 * Таблица RevShare кампаний криейтора.
 * Использует API /staff/ref-share/orders с фильтром client=creatorId
 */
const CreatorRevShareCampaignsTable: React.FC<CreatorRevShareCampaignsTableProps> = ({ creatorId }) => {
  const { t } = useTranslations();
  const [orders, setOrders] = useState<RefShareOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<RevShareFilters>(DEFAULT_FILTERS);
  const [localSearch, setLocalSearch] = useState('');
  const [sortBy, setSortBy] = useState<string>('created_at'); // created_at = newest first

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: localSearch }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Sync local search with filters
  useEffect(() => {
    setLocalSearch(filters.search);
  }, [filters.search]);

  // Status options
  const statusOptions = useMemo(() => [
    { key: 'all', label: t('admin.revshareCampaigns.filters.status.all') || 'Все статусы' },
    { key: 'in_progress', label: 'Активная' },
    { key: 'in_moderation', label: 'На модерации' },
    // { key: 'waiting', label: 'Принята' },
    { key: 'completed', label: 'Неактивная' },
    { key: 'canceled', label: 'Отменена' },
  ], [t]);

  const apiParams = useMemo(() => {
    const params: Record<string, unknown> = {
      page: currentPage,
      page_size: pageSize,
      client: creatorId,
    };

    if (filters.search) {
      params.search = filters.search;
    }

    if (filters.status !== 'all') {
      params.status = filters.status as OrderStatus;
    }

    if (sortBy) {
      params.sort_by = sortBy;
    }

    return params;
  }, [currentPage, pageSize, creatorId, filters, sortBy]);

  // Calculate total platform income
  const totalPlatformIncome = useMemo(() => {
    return orders.reduce((sum, order) => {
      const totalRevenue = Number(order.transactions_statistic?.total || 0);
      return sum + (totalRevenue * (PLATFORM_COMMISSION / 100));
    }, 0);
  }, [orders]);

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await refShareApi.getOrders(apiParams);
      setOrders(response.objects || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error("Error fetching RevShare orders:", error);
      setOrders([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [apiParams]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Handle status change
  const handleStatusChange = useCallback(async (orderId: string, newStatus: OrderStatus) => {
    try {
      await refShareApi.changeOrderStatus(orderId, newStatus);
      // Refetch orders to update the list
      fetchOrders();
      showPartnerSuccess(
        newStatus === 'in_progress'
          ? 'Кампания успешно активирована'
          : newStatus === 'canceled'
          ? 'Кампания отменена'
          : 'Статус кампании изменён'
      );
    } catch (error) {
      showError(parseApiError(error, 'Не удалось изменить статус кампании'));
    }
  }, [fetchOrders]);

  const handleFilterChange = useCallback((key: keyof RevShareFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setCurrentPage(1);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return filters.search !== '' || filters.status !== 'all';
  }, [filters]);

  const handleSort = useCallback((newSortBy: string) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
  }, []);

  // Table columns
  const columns = useMemo(() => [
    { key: 'id', name: 'ID кампании' },
    // { key: 'owner', name: 'Владелец' },
    { key: 'campaign', name: 'Кампания' },
    { key: 'statistics', name: 'Статистика' },
    { key: 'platformIncome', name: `Доход ($${totalPlatformIncome.toFixed(2)})` },
    { key: 'status', name: 'Статус', sortable: true, sortKey: 'created_at' },
    { key: 'marketer', name: 'Маркетолог' },
    { key: 'actions', name: 'Действия' },
  ], [totalPlatformIncome]);

  // Cell renderer
  const renderCell = useCallback((order: RefShareOrder, columnKey: string) => {
    const orderId = order._id || order.id || '';

    switch (columnKey) {
      case 'id':
        return (
          <IdView textToCopy={orderId}>
            {orderId.slice(-4)}
          </IdView>
        );

      // case 'owner':
      //   return <OwnerCell order={order} />;

      case 'campaign':
        return <CampaignCell order={order} />;

      case 'statistics':
        return <CampaignStatisticsCell order={order} />;

      case 'platformIncome':
        return <PlatformIncomeCell order={order} />;

      case 'status':
        return <CampaignStatusCell order={order} />;

      case 'marketer':
        return <MarketerCell order={order} />;

      case 'actions':
        return <CampaignActionsCell order={order} onStatusChange={handleStatusChange} />;

      default:
        return null;
    }
  }, [handleStatusChange]);

  const emptyContent = (
    <div className="text-center py-[48px]">
      <div className="flex flex-col items-center gap-[16px]">
        <EmptyContentIc />
        <div className="flex flex-col gap-[6px]">
          <Typography variant="h6" className="text-[20px] font-semibold leading-[28px]">
            {t('admin.revshareCampaigns.table.empty.title') || 'Нет RevShare кампаний'}
          </Typography>
          <Typography className="text-[#A1A1AA] text-[16px] font-medium leading-[24px]" variant="body1">
            {t('admin.revshareCampaigns.table.empty.description') || 'У этого криейтора пока нет RevShare кампаний'}
          </Typography>
        </div>
      </div>
    </div>
  );

  // Loading state
  if (isLoading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Typography variant="h6" className="flex-shrink-0 mb-[16px]">
        RevShare кампании
      </Typography>
      {/* Filters Section */}
      <div className="pb-[24px] flex items-center gap-[16px] flex-wrap justify-between w-full flex-shrink-0">
        <div className="flex items-center gap-[16px] flex-wrap justify-start">
          <Input
            type="text"
            placeholder={t('admin.revshareCampaigns.filters.search.placeholder') || 'Поиск...'}
            size="sm"
            value={localSearch}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalSearch(e.target.value)}
            endContent={<SearchInputIc />}
            classNames={{
              mainWrapper: "w-[273px]",
              base: "w-[273px]",
              input: "text-sm",
              inputWrapper: "rounded-lg h-[36px] w-[273px]",
            }}
          />

          <FilterSelect
            placeholder={t('admin.revshareCampaigns.filters.status.placeholder') || 'Статус'}
            options={statusOptions}
            selectedKey={filters.status}
            variant="campaign"
            onSelectionChange={(value) => handleFilterChange('status', value)}
            startContent={<DangerIc size={20} />}
            autoWidth={true}
          />
          {hasActiveFilters && (
            <Tooltip content={t('admin.revshareCampaigns.filters.resetTooltip') || 'Сбросить фильтры'} placement="top">
              <Button
                size="sm"
                className="h-[36px] rounded-[8px] !w-[36px] p-0 bg-[#F4F4F5] min-w-[36px]"
                onPress={handleResetFilters}
              >
                <ResetFiltersIc />
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-hidden">
        <UniversalTable
          data={orders}
          columns={columns}
          renderCell={renderCell}
          emptyContent={emptyContent}
          pagination={{
            count: totalCount,
            page_size: pageSize,
            page: currentPage,
          }}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          currentPage={currentPage}
          currentPageSize={pageSize}
          sortBy={sortBy}
          onSort={handleSort}
        />
      </div>
    </div>
  );
};

export default CreatorRevShareCampaignsTable;
